import { Component, OnInit, Inject, OnDestroy, Input, ViewChild, ElementRef } from '@angular/core';

import { Subscription, combineLatest} from 'rxjs';

import { PaginatorServiceInterface } from '../../services/paginator.service.interface';
import { PageCriteriaModelInterface } from '../../models/page-criteria.model.interface';

@Component({
    selector: 'agpr-minimal-paginator',
    templateUrl: './minimal-paginator.component.html',
    styleUrls: ['../Paginator/paginator.component.scss', './minimal-paginator.component.scss'],
    providers: [],
    standalone: false
})
export class MinimalPaginatorComponent implements OnInit, OnDestroy {
    @Input() id: string = null;
    @Input() isDisabled: boolean = false;
    @ViewChild('pageInput', { static: true }) pageInput: ElementRef;

    itemsPerPage: number = 25;
    pageNumber: number = 1;
    totalNumberOfPages: number = 1;
    totalCount: number = null;

    backButtonDisabled: boolean = true;
    forwardButtonDisabled: boolean = false;

    private subscriptions: Subscription[] = [];
    private pageCriteria: PageCriteriaModelInterface;

    constructor(@Inject('PaginatorServiceInterface') public paginatorService: PaginatorServiceInterface) {}

    ngOnInit() {
        this.subscriptions.push(
            combineLatest([
                this.paginatorService.getPageCriteria(),
                this.paginatorService.getItemCount()
            ]).subscribe(
                ([searchCriteria, count]) => {
                    if (searchCriteria) {
                        this.pageCriteria = searchCriteria;
                        this.pageNumber = searchCriteria.page;
                        this.itemsPerPage = searchCriteria.pageSize || 25;
                        this.enableDisableForwardAndBackButtons();
                    }
                    if (count !== null) {
                        // calculate a whole number for total pages
                        this.itemsPerPage = searchCriteria.pageSize || 25;
                        this.totalNumberOfPages = Math.ceil(count / this.itemsPerPage);
                        this.paginatorService.setMaxPageCriteria(this.totalNumberOfPages);
                        // clamp pagenumber
                        this.pageNumber = Math.max(1, Math.min(this.totalNumberOfPages, this.pageNumber));
                        this.totalCount = count;
                        this.enableDisableForwardAndBackButtons();
                    } else {
                        this.itemsPerPage = 25;
                        this.pageNumber = 1;
                        this.totalNumberOfPages = 1;
                        this.enableDisableForwardAndBackButtons();
                    }
                }
            )
        );
    }

    pageBackClick(): void {
        this.paginatorService.canSetPageCriteria()
            .subscribe((can) => {
                if (can) {
                    if (this.pageCriteria.page > 0) {
                        this.pageCriteria.page--;
                        this.paginatorService.setPageCriteria(this.pageCriteria);
                    }
                }
            });
    }

    pageForwardClick(): void {
        this.paginatorService.canSetPageCriteria()
            .subscribe((can) => {
                if (can) {
                    this.pageCriteria.page++;
                    this.paginatorService.setPageCriteria(this.pageCriteria);
                }
            });
    }

    /**
     * Enables/Disables forward and back buttons as appropriate
     */
    enableDisableForwardAndBackButtons(): void {
        this.backButtonDisabled = (this.pageNumber <= 1);

        // when total mention count < max mention count, user can only navigate to the last page
        this.forwardButtonDisabled = (this.pageNumber >= this.totalNumberOfPages);
    }

    /**
     * Handles keyup events on Input box.  If keyup event on enter key calls onInput
     * @param event input box keyup event
     */
    onKeyUp(event): void {
        if (event.key === 'Enter') {
            this.onInput(event);
        }
    }

    /**
     * Validates input from Manual Page Number entry and updates Paginator Service
     * @param event input $event
     */
    onInput(event): void {
        const newPage = +event.target.value;
        if (newPage === this.pageNumber) {
            return;
        }
        this.paginatorService.canSetPageCriteria()
            .subscribe((can) => {
                if (can) {
                    if (1 <= newPage && newPage <= this.totalNumberOfPages && newPage !== this.pageNumber) { // input is valid
                        this.pageNumber = newPage;
                        this.pageCriteria.page = this.pageNumber;
                        this.paginatorService.setPageCriteria(this.pageCriteria);
                    } else {
                        this.pageNumber = this.pageCriteria.page;
                        this.pageInput.nativeElement.value = this.pageNumber;
                    }
                } else {
                    this.pageNumber = this.pageCriteria.page;
                }
            });
    }

    ngOnDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
}
